import { useState } from 'react';
import { hotComments } from '@/mocks/gua88-site';
import { SectionHead } from './articleUi';

const avatarBg = ['bg-primary-100 text-primary-700', 'bg-accent-100 text-accent-900', 'bg-secondary-100 text-secondary-900'];
const pollInit = [
  { label: '还会再度反转', votes: 412 },
  { label: '逐渐降温收尾', votes: 268 },
  { label: '进入漫长拉锯', votes: 331 },
];

type Hot = (typeof hotComments)[number];

export default function Talk() {
  const [comments, setComments] = useState<Hot[]>(hotComments);
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [text, setText] = useState('');
  const [poll, setPoll] = useState(pollInit);
  const [picked, setPicked] = useState<number | null>(null);

  const toggleLike = (name: string) => {
    setLiked((m) => {
      const next = { ...m, [name]: !m[name] };
      return next;
    });
    setComments((list) =>
      list.map((c) =>
        c.name === name ? { ...c, like: c.like + (liked[name] ? -1 : 1) } : c,
      ),
    );
  };

  const totalPoll = poll.reduce((s, p) => s + p.votes, 0);

  const postComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    const entry: Hot = {
      name: '我',
      level: '刚蹲的新猹',
      time: '刚刚',
      like: 0,
      text: text.trim(),
    };
    setComments((list) => [entry, ...list]);
    setText('');
  };

  return (
    <section id="talk" className="border-b border-background-200 bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <SectionHead
          kicker="Talk · 一起蹲"
          title="热评与走向投票"
          desc="不同观点都值得被听见，也欢迎你在下方补充分享自己的线索与看法。发言请基于事实、保持克制。"
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px]">
          {/* comments */}
          <div>
            {/* compose */}
            <form
              onSubmit={postComment}
              className="mb-6 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-start"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-background-50">
                我
              </span>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={2}
                maxLength={200}
                placeholder="分享你的看法或补充分线索…（限 200 字）"
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-300"
              />
              <button
                type="submit"
                className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                发表
              </button>
            </form>

            <div className="flex flex-col">
              {comments.map((c, i) => (
                <article
                  key={c.name + i}
                  className="flex gap-3 border-b border-background-200 py-5 first:pt-0"
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${avatarBg[i % 3]}`}
                  >
                    {c.name.slice(0, 1)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-foreground-950">{c.name}</span>
                      <span className="rounded-full bg-background-200 px-2 py-0.5 text-[11px] text-foreground-600">
                        {c.level}
                      </span>
                      <span className="text-xs text-foreground-400">{c.time}</span>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{c.text}</p>
                    <div className="mt-2 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => toggleLike(c.name)}
                        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs transition-colors ${
                          liked[c.name]
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-background-200 text-foreground-600 hover:bg-secondary-100'
                        }`}
                      >
                        <i
                          className={`${liked[c.name] ? 'ri-heart-fill' : 'ri-heart-line'}`}
                        ></i>
                        {c.like}
                      </button>
                      <button
                        type="button"
                        className="ml-auto inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs text-foreground-500 transition-colors hover:bg-background-200"
                      >
                        <i className="ri-reply-line"></i>回复
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* poll + rules */}
          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">
                <i className="ri-bar-chart-grouped-line mr-1.5 align-middle text-accent-500"></i>
                你觉得 88 后续会怎么走？
              </h3>
              <p className="mt-1 text-xs text-foreground-600">选择后即可查看实时占比</p>
              <div className="mt-5 space-y-3">
                {poll.map((p, idx) => {
                  const pct = totalPoll ? Math.round((p.votes / totalPoll) * 100) : 0;
                  const active = picked === idx;
                  return (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => {
                        if (picked === null) {
                          setPicked(idx);
                          setPoll((arr) => arr.map((it, k) => (k === idx ? { ...it, votes: it.votes + 1 } : it)));
                        }
                      }}
                      disabled={picked !== null}
                      className="block w-full"
                    >
                      <span className="mb-1 flex items-center justify-between text-sm">
                        <span className={active ? 'font-medium text-primary-700' : 'text-foreground-700'}>
                          <i
                            className={`mr-1.5 ${active ? 'ri-checkbox-circle-fill text-primary-500' : 'ri-checkbox-blank-circle-line text-foreground-300'}`}
                          ></i>
                          {p.label}
                        </span>
                        <span className="text-xs text-foreground-500">{p.votes} 票 · {pct}%</span>
                      </span>
                      <span className="block h-2 overflow-hidden rounded-full bg-background-200">
                        <span
                          className={`block h-full rounded-full transition-all duration-500 ${
                            idx === 0 ? 'bg-accent-500' : idx === 1 ? 'bg-secondary-500' : 'bg-primary-500'
                          }`}
                          style={{ width: picked === null ? '0%' : `${pct}%` }}
                        ></span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">
                <i className="ri-mental-health-line mr-1.5 align-middle text-secondary-900"></i>
                理性吃瓜公约
              </h3>
              <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-secondary-900">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-secondary-900"></i>
                  不发布未经证实的私密信息与人肉内容
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-secondary-900"></i>
                  不攻击、不骚扰事件当事人及其家人
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-secondary-900"></i>
                  引用内容注明出处，指正误读而非对立
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}